import { List as ImmutableList } from 'immutable';
import { DateTime } from 'luxon';
import { createSelector } from 'reselect';

import type RootState from 'app-components/utils/redux-store/rootState';

import { type AccountsValueChangesSelectorsState, accountsValueChangesSelectors } from '../accounts-value-changes';
import { type DATE_REDUCER_KEY, dateSelectors } from '../date';
import {
  ACCOUNTS_BALANCES_HISTORY_REDUCER_KEY,
  type AccountBalanceType,
  type AccountsBalancesHistoryColumnCollection,
  type AccountsBalancesHistoryRowList,
  type AccountsBalancesHistoryStore,
  type BalanceRow,
  type DatedCellData,
} from './accountsBalancesHistoryTypes';

export type AccountsBalancesHistorySelectorsState = Pick<RootState, typeof ACCOUNTS_BALANCES_HISTORY_REDUCER_KEY>;

type AccountsBalancesHistoryState = AccountsBalancesHistorySelectorsState & AccountsValueChangesSelectorsState;
type AccountsBalancesHistoryWithDateState = AccountsBalancesHistorySelectorsState &
  Pick<RootState, typeof DATE_REDUCER_KEY>;
type AccountIds = readonly string[];

type AccountSelectionProps = {
  accounts?: AccountIds;
};

export type AccountsBalancesHistoryTableSelectorProps = {
  accounts: readonly string[];
  endDate?: string | null;
  orderBy: string;
  sortDirection?: 'asc' | 'desc';
  startDate?: string | null;
};

export type BalancesHistoryChartData = {
  containsBalances: boolean;
  date: string;
  isEstimated?: boolean;
  [field: string]: boolean | number | string | undefined;
};

export type InvestmentBalancesData = {
  relevantBalancesData: DatedCellData[];
  selectedDatePercentChange: number | null | undefined;
  selectedDateValueChange: number | null | undefined;
  todayBalance: number | null | undefined;
};

export const getStore = (state: AccountsBalancesHistorySelectorsState): AccountsBalancesHistoryStore =>
  state[ACCOUNTS_BALANCES_HISTORY_REDUCER_KEY];

export const getLoadPending = (state: AccountsBalancesHistorySelectorsState): boolean => getStore(state).loadPending;
export const getIsLoading = (state: AccountsBalancesHistorySelectorsState): boolean => getStore(state).isLoading;
export const getError = (state: AccountsBalancesHistorySelectorsState): unknown => getStore(state).error;
export const getRows = (state: AccountsBalancesHistorySelectorsState): AccountsBalancesHistoryRowList | undefined =>
  getStore(state).rows;
export const getColumns = (
  state: AccountsBalancesHistorySelectorsState,
): AccountsBalancesHistoryColumnCollection | undefined => getStore(state).columns;
export const getResourcesById = getRows;

const { getDailyValueChanges } = accountsValueChangesSelectors;

export const addDateFieldstoCellData = createSelector([getRows, getColumns], (balanceRows, balanceColumns) => {
  const hasNoColumns = Array.isArray(balanceColumns) && balanceColumns.length === 0;
  if (!balanceRows || !balanceColumns || hasNoColumns) {
    return ImmutableList<AccountBalanceType>();
  }

  return balanceRows.map((row) => {
    const newRow = { ...row };
    newRow.cellData = newRow.cellData || [];
    balanceColumns.forEach((column) => {
      const existingCell = newRow.cellData?.find((cell) => cell.columnId === column.id);
      if (!existingCell) {
        newRow.cellData?.push({
          id: column.id,
          columnId: column.id,
          value: null,
          date: column.field || null,
          fieldName: column.headerName || null,
        });
      } else {
        existingCell.date = column.field || null;
        existingCell.fieldName = column.headerName || null;
      }
    });
    newRow.cellData.sort((valueA, valueB) => valueA.columnId - valueB.columnId);
    return newRow;
  });
});

export const getBalancesHistoryTotals = createSelector(
  [addDateFieldstoCellData],
  (modifiedRows): AccountBalanceType | undefined => modifiedRows.find((row) => row.name === 'Total'),
);

export const getSortedBalancesHistoryByAccount = createSelector(
  [(_state: AccountsBalancesHistorySelectorsState, accounts?: AccountIds) => accounts, addDateFieldstoCellData],
  (accounts, modifiedData): AccountsBalancesHistoryRowList => {
    const accountsBalances = modifiedData.filter(
      (account) => account.entityId !== undefined && Boolean(accounts?.includes(account.entityId)),
    );
    accountsBalances.forEach((accountBalance, index) => {
      accountBalance.cellData?.sort(
        (valueA, valueB) =>
          DateTime.fromISO(valueA.date ?? '').toMillis() - DateTime.fromISO(valueB.date ?? '').toMillis(),
      );
      accountBalance.colorIndex = index % 11;
    });
    return accountsBalances;
  },
);

export const getDateRange = createSelector([getColumns], (columnData): string[] => {
  const dateRange: string[] = [];
  columnData?.forEach((column) => {
    if (column.field) dateRange.push(column.field);
  });
  return dateRange;
});

export const formatBalancesForChart = createSelector(
  [
    getDateRange,
    (state: AccountsBalancesHistorySelectorsState, props: AccountSelectionProps) =>
      getSortedBalancesHistoryByAccount(state, props.accounts),
  ],
  (selectedDateRange, accountData): BalancesHistoryChartData[] =>
    selectedDateRange.map((myDate) => {
      const result: BalancesHistoryChartData = { date: myDate, containsBalances: false };
      accountData.forEach((account) => {
        if (!account.cellData || account.entityId === undefined) return;

        const cell = account.cellData.find((balance) => balance.date === myDate);
        if (cell && cell.value !== null) {
          result[`${account.entityId}-balance`] = cell.value;
          result.isEstimated = Boolean(cell.isEstimated);
          result.containsBalances = true;
        }
      });
      return result;
    }),
);

export const getZillowHomeValueHistory = createSelector(
  [getBalancesHistoryTotals],
  (balanceTotal): DatedCellData[] | undefined =>
    balanceTotal?.cellData?.filter((item): item is DatedCellData => Boolean(item.date)),
);

const hasBalanceRowKey = (row: BalanceRow, key: string): key is keyof BalanceRow => key in row;

export const getSortedBalancesForTable = createSelector(
  [
    (state: AccountsBalancesHistoryState, props: AccountsBalancesHistoryTableSelectorProps) =>
      getSortedBalancesHistoryByAccount(state, props.accounts),
    (state: AccountsBalancesHistoryState) => getDailyValueChanges(state),
    (_state: AccountsBalancesHistoryState, props: AccountsBalancesHistoryTableSelectorProps) => props.startDate,
    (_state: AccountsBalancesHistoryState, props: AccountsBalancesHistoryTableSelectorProps) => props.endDate,
    (_state: AccountsBalancesHistoryState, props: AccountsBalancesHistoryTableSelectorProps) => props.orderBy,
    (_state: AccountsBalancesHistoryState, props: AccountsBalancesHistoryTableSelectorProps) => props.sortDirection,
  ],
  (sortedBalances, dailyValueChanges, startDate, endDate, orderBy, sortDirection): ImmutableList<BalanceRow> => {
    const formattedBalances: BalanceRow[] = [];

    sortedBalances.forEach((account) => {
      const { colorIndex, entityId: accountId, name: accountName } = account;
      if (colorIndex === undefined || accountId === undefined) return;

      if (!account.cellData || account.cellData.length === 0) {
        formattedBalances.push({
          colorIndex,
          accountName,
          accountId,
          oneDayBalance: null,
          oneDayPercent: null,
          specialDayBalance: null,
          specialDayPercent: null,
          todayBalance: null,
        });
        return;
      }

      const valueChange = dailyValueChanges?.find((item) => item?.entityId === accountId);
      const oneDayBalance = valueChange ? valueChange.cellData?.[0]?.value : null;
      const oneDayPercent = valueChange ? valueChange.cellData?.[1]?.value : null;
      const todayBalance = valueChange ? valueChange.cellData?.[2]?.value : null;

      const specialStartDay = account.cellData.find((day) => day.date === startDate)?.value;
      const specialEndDay = account.cellData.find((day) => day.date === endDate)?.value;
      const specialDayBalance =
        typeof specialStartDay === 'number' && typeof specialEndDay === 'number'
          ? specialEndDay - specialStartDay
          : null;
      const specialDayPercent =
        typeof specialStartDay === 'number' && typeof specialEndDay === 'number' && specialStartDay
          ? ((specialEndDay - specialStartDay) / Math.abs(specialStartDay)) * 100
          : null;

      formattedBalances.push({
        colorIndex,
        accountId,
        accountName,
        oneDayBalance,
        oneDayPercent,
        specialDayBalance,
        specialDayPercent,
        todayBalance,
      });
    });

    formattedBalances.sort((unalteredValueA, unalteredValueB) => {
      if (orderBy === 'accountName') {
        const valueA = unalteredValueA.accountName || 'ZZZZZZZZZZZZZZ';
        const valueB = unalteredValueB.accountName || 'ZZZZZZZZZZZZZZ';
        return sortDirection === 'asc'
          ? valueA.localeCompare(valueB, undefined)
          : valueB.localeCompare(valueA, undefined);
      }

      const rawValueA = hasBalanceRowKey(unalteredValueA, orderBy) ? unalteredValueA[orderBy] : undefined;
      const rawValueB = hasBalanceRowKey(unalteredValueB, orderBy) ? unalteredValueB[orderBy] : undefined;
      const valueA = rawValueA != null ? Number(rawValueA) : -99999999999999;
      const valueB = rawValueB != null ? Number(rawValueB) : -99999999999999;

      // Using regular numeric comparisons because localeCompare numeric does not account for negative values.
      return sortDirection === 'asc' ? valueB - valueA : valueA - valueB;
    });

    return ImmutableList(formattedBalances);
  },
);

export const getInvestmentBalancesData = createSelector(
  [
    (state: AccountsBalancesHistoryWithDateState) => getBalancesHistoryTotals(state),
    (state: AccountsBalancesHistoryWithDateState) => dateSelectors.getCurrentDateString(state),
    (_state: AccountsBalancesHistoryWithDateState, isDashboard?: boolean) => isDashboard,
  ],
  (accountsBalance, currentDate, isDashboard): Partial<InvestmentBalancesData> => {
    if (!accountsBalance?.cellData) return {};
    const accountsBalanceTotalCells = accountsBalance.cellData;

    const valueFieldName = `${isDashboard ? 'Daily ' : ''}Value Change`;
    const percentFieldName = `${isDashboard ? 'Daily ' : ''}Percent Change`;

    const relevantBalancesData = accountsBalanceTotalCells.filter((item): item is DatedCellData => Boolean(item.date));
    const selectedDateValueChange = accountsBalanceTotalCells.find((item) => item.fieldName === valueFieldName)?.value;
    const selectedDatePercentChange = accountsBalanceTotalCells.find(
      (item) => item.fieldName === percentFieldName,
    )?.value;
    const todayBalance =
      accountsBalanceTotalCells.find((item) => item.date === currentDate)?.value || relevantBalancesData.at(-1)?.value;

    return {
      relevantBalancesData,
      selectedDateValueChange,
      selectedDatePercentChange,
      todayBalance,
    };
  },
);
